import { type FC, useCallback, useMemo } from "react"
import { Navigate, useParams } from "react-router"

import EditStudentEmail, { type EmailSaveData } from "~/components/EditStudentEmail"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const StudentEmailDetailSettingsPage: FC = () => {
	const { emailId } = useParams()

	const { data: layout, error: layoutError } = query.studentEmails.getLayout.useQuery()
	const { data: email, error: emailError, refetch } = query.studentEmails.get.useQuery(emailId ?? "")

	const handleSave = useCallback(
		async (data: EmailSaveData) => {
			if (email == null) return
			await trpc.studentEmails.update.mutate({ emailId: email.id, data: { custom: data } })
			await refetch()
		},
		[email, refetch],
	)

	const placeholders = useMemo(
		() =>
			email?.placeholders.map((x: { key: string; previewValue: string }) => ({
				key: x.key,
				value: x.previewValue,
			})) ?? [],
		[email?.placeholders],
	)

	if (email === null) {
		return <Navigate to={routes.settings.studentEmail.index()} replace />
	}

	return (
		<Layout>
			<PageBreadcrumb
				title={email?.label ?? "..."}
				path={[
					{ title: "Settings", link: routes.settings.index() },
					{ title: "Email Automation", link: routes.settings.studentEmail.index() },
				]}
			/>
			{layoutError || emailError ? (
				<p>Failed to load email.</p>
			) : !layout || !email ? (
				<PartialLoadingPage />
			) : (
				<EditStudentEmail
					email={email}
					placeholders={placeholders}
					layout={layout.content}
					aiTarget={{ type: "standard", id: email.id }}
					onSave={handleSave}
				/>
			)}
		</Layout>
	)
}

export default StudentEmailDetailSettingsPage
